import { Controller } from "@hotwired/stimulus"

/**
 * StyleTreeController – 2-level hierarchical multi-select tree for dance styles.
 *
 * Levels:
 *   1. Category  (e.g. "Towarzyski International")  – UI-only checkbox
 *   2. Item      – either a group (ST, LA, American Smooth …) or a single style (Salsa, Bachata …)
 *
 * For grouped categories (ballroom_international / ballroom_american):
 *   Each item is a group. The group shows its dances as descriptive text:
 *     "ST (Walc angielski, Tango, Walc wiedeński, Slowfox, Quickstep)"
 *   If the group wraps a "group-style" (like ST, LA), the checkbox submits
 *   that group-style's UUID directly.
 *   If the group has no group-style (American Smooth / Rhythm), hidden inputs
 *   for all child-style UUIDs are toggled on/off with the checkbox.
 *
 * For flat categories (latin_caribbean_social, tango_social …):
 *   Each item is a single dance style with its own checkbox.
 *
 * Counter badge next to the category name shows:
 *   – selected dance count when ≥ 1 item is checked
 *   – total dance count when nothing is checked (informational hint)
 */
export default class extends Controller {
  static targets = [
    "categoryBox",   // category-level checkbox (UI-only, no form name)
    "itemBox",       // item-level checkbox (group or single style)
    "hiddenInput",   // hidden inputs for non-group-style groups
    "catChildren",   // collapsible wrapper for category children
    "counter"        // counter badge
  ]

  connect() {
    this.syncAll()
  }

  /* ────────────── Category ────────────── */

  toggleCategory(event) {
    const cat     = event.target.dataset.cat
    const checked = event.target.checked

    this.itemBoxTargets
      .filter(cb => cb.dataset.cat === cat)
      .forEach(cb => {
        cb.checked = checked
        this._syncHiddenInputs(cb)
      })

    this.updateCounter(cat)
  }

  /* ────────────── Item (group or style) ─ */

  toggleItem(event) {
    const cat = event.target.dataset.cat

    this._syncHiddenInputs(event.target)
    this.syncCategory(cat)
    this.updateCounter(cat)
  }

  /* ────────────── Collapse / Expand ───── */

  toggleCollapse(event) {
    event.preventDefault()
    const cat = event.currentTarget.dataset.cat
    const box = this.catChildrenTargets.find(el => el.dataset.cat === cat)
    if (box) {
      box.classList.toggle("d-none")
      // Find the icon within the clicked element or its parent header
      const header = event.currentTarget.closest('.style-tree__header') || event.currentTarget
      this._flipIcon(header)
    }
  }

  /** Prevent checkbox click from bubbling to the row's collapse handler */
  stopPropagation(event) {
    event.stopPropagation()
  }

  /* ────────────── Sync helpers ────────── */

  syncCategory(cat) {
    const catBox = this.categoryBoxTargets.find(cb => cb.dataset.cat === cat)
    if (!catBox) return

    const items      = this.itemBoxTargets.filter(cb => cb.dataset.cat === cat)
    const allChecked = items.length > 0 && items.every(cb => cb.checked)
    const anyChecked = items.some(cb => cb.checked)

    catBox.checked       = allChecked
    catBox.indeterminate = anyChecked && !allChecked
  }

  updateCounter(cat) {
    const counter = this.counterTargets.find(el => el.dataset.cat === cat)
    if (!counter) return

    const items    = this.itemBoxTargets.filter(cb => cb.dataset.cat === cat)
    const total    = items.reduce((s, cb) => s + this._danceCount(cb), 0)
    const selected = items
      .filter(cb => cb.checked)
      .reduce((s, cb) => s + this._danceCount(cb), 0)

    counter.textContent = selected > 0 ? selected : total
  }

  syncAll() {
    const cats = new Set(this.categoryBoxTargets.map(cb => cb.dataset.cat))
    cats.forEach(cat => {
      this.itemBoxTargets
        .filter(cb => cb.dataset.cat === cat)
        .forEach(cb => this._syncHiddenInputs(cb))

      this.syncCategory(cat)
      this.updateCounter(cat)
    })
  }

  /* ────────────── Private ─────────────── */

  /** Enable / disable hidden inputs tied to a non-group-style checkbox */
  _syncHiddenInputs(checkbox) {
    const itemKey = checkbox.dataset.item
    if (!itemKey) return

    const cat = checkbox.dataset.cat
    this.hiddenInputTargets
      .filter(i => i.dataset.cat === cat && i.dataset.item === itemKey)
      .forEach(i => { i.disabled = !checkbox.checked })
  }

  _danceCount(checkbox) {
    return parseInt(checkbox.dataset.danceCount || "1", 10)
  }

  _flipIcon(el) {
    const icon = el.querySelector(".collapse-icon")
    if (icon) {
      icon.classList.toggle("fa-chevron-down")
      icon.classList.toggle("fa-chevron-right")
    }
  }
}
